import { inject, Injectable } from '@angular/core';
import { PreorderedProductService, SqliteService } from '@app/services';
import {
    CartItemModel,
    PreorderedProductModel,
    ProductCategoryModel,
    ProductModel,
    RouteModel,
    StoreModel
} from '@app/models';

@Injectable({
    providedIn: 'root',
})
export class PreorderedProductRepository
{
    private readonly keys = {
        tableName: 'preordered_items',
        productsTable: 'products',
        productCategoryTableName: 'product_categories',
    };
    private sqliteService = inject(SqliteService);
    private preorderedProductService = inject(PreorderedProductService);

    async downloadPreorderedProducts()
    {
        const items = await this.preorderedProductService.getPreorderedProducts();

        if (items.length > 0)
        {
            await this.savePreorderedProducts(items);
        }

        return items;
    }

    async savePreorderedProducts(items: PreorderedProductModel[]): Promise<void>
    {
        const columns = [
            'id',
            'external_id',
            'store_external_id',
            'route_external_id',
            'product_external_id',
            'quantity',
            'price',
            'created_at',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < items.length; i += batchSize)
        {
            const chunk = items.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(id) DO
                UPDATE
                    SET
                        external_id = excluded.external_id,
                    quantity = excluded.quantity,
                    price = excluded.price,
                    created_at = excluded.created_at,
                    route_external_id = excluded.route_external_id,
                    product_external_id = excluded.product_external_id,
                    store_external_id = excluded.store_external_id;
            `;
            const values: any[] = chunk.flatMap((p) => [
                p.id,
                p.external_id,
                p.store_external_id,
                p.route_external_id,
                p.product_external_id,
                p.quantity,
                p.price,
                new Date().toISOString(),
            ]);
            await this.sqliteService.save(sql, values);
        }
    }

    async isTherePreorderedProductsForTheStoreRoute(store: StoreModel, route: RouteModel): Promise<boolean>
    {
        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE store_external_id = ?
              AND route_external_id = ? AND DATE(created_at) = DATE('now') LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [store.external_id, route.external_id]) ?? [];
        const items = data as PreorderedProductModel[];

        return items.length > 0;
    }

    async getPreorderedProducts(routeExternalId: string, storeExternalId: string): Promise<CartItemModel[]>
    {
        const sql = `
            SELECT ${this.keys.productsTable}.*,
                   pc.id          as category_id,
                   pc.name        as category_name,
                   pc.external_id as category_external_id,
                   pio.quantity   as quantity,
                   pio.price      as price
            FROM ${this.keys.productsTable}
                     JOIN ${this.keys.tableName} pio ON pio.product_external_id = ${this.keys.productsTable}.external_id
                     LEFT JOIN ${this.keys.productCategoryTableName} pc ON ${this.keys.productsTable}.product_category_id = pc.id
            WHERE DATE(pio.created_at) = DATE('now')
              AND pio.route_external_id = ?
              AND pio.store_external_id = ?
            ORDER BY ${this.keys.productsTable}.sort_order IS NULL, ${this.keys.productsTable}.sort_order, ${this.keys.productsTable}.name ASC;
        `;
        const values = [routeExternalId, storeExternalId];
        const data = await this.sqliteService.get(sql, values) ?? [];
        const cartItems: CartItemModel[] = [];

        for (const row of data)
        {
            if (row.price !== null)
            {
                const product: ProductModel = {
                    id: row.id,
                    product_category_id: row.product_category_id,
                    external_id: row.external_id,
                    name: row.name,
                    barcode: row.barcode,
                    image_path: row.image_path,
                    price: row.price,
                    sort_order: row.sort_order,
                    bu: row.bu,
                    product_category: row.category_id
                        ? {
                            id: row.category_id,
                            name: row.category_name,
                            external_id: row.category_external_id
                        } as ProductCategoryModel
                        : null,
                };
                const item: CartItemModel = {
                    product,
                    quantity: row.quantity,
                    subtotal: row.price * row.quantity,
                };
                cartItems.push(item);
            }
        }

        return cartItems;
    }
}
